'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AXES, Axis, AppConfig, Photo } from '@/lib/types';

const BLUE = 'rgb(51, 102, 204)';
const AHEAD = 5; // photos to analyze ahead of the one being reviewed

type Result = Photo | 'loading' | { error: string };

interface SessionStats {
  reviewed: number;
  right: number;
  fixed: number;
}

export function TrainDialog({
  dir,
  sampleSize,
  config,
  onClose,
  onFinish,
}: {
  dir: string;
  sampleSize: number;
  config: AppConfig;
  onClose: () => void;
  onFinish: (message: string) => void;
}) {
  const [ids, setIds] = useState<number[] | null>(null);
  const [totalInFolder, setTotalInFolder] = useState(0);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Record<number, Result>>({});
  const [picked, setPicked] = useState<Record<Axis, string> | null>(null);
  const [stats, setStats] = useState<SessionStats>({ reviewed: 0, right: 0, fixed: 0 });
  const [fatal, setFatal] = useState('');
  const startedRef = useRef(false);
  const launchedIds = useRef(new Set<number>());

  /** Launch classification for the current photo plus up to AHEAD more. */
  const topUp = useCallback((list: number[], from: number) => {
    for (const id of list.slice(from, from + 1 + AHEAD)) {
      if (launchedIds.current.has(id)) continue;
      launchedIds.current.add(id);
      setResults((p) => ({ ...p, [id]: 'loading' }));
      fetch('/api/train/classify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      })
        .then(async (res) => {
          const data = await res.json();
          if (!res.ok) throw new Error(data.error ?? 'classification failed');
          setResults((p) => ({ ...p, [id]: data.photo as Photo }));
        })
        .catch((e) => {
          setResults((p) => ({ ...p, [id]: { error: e instanceof Error ? e.message : String(e) } }));
        });
    }
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const res = await fetch('/api/train/sample', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dir, n: sampleSize }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error ?? 'sampling failed');
        setIds(data.ids);
        setTotalInFolder(data.totalInFolder);
        topUp(data.ids, 0);
      } catch (e) {
        setFatal(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [dir, sampleSize, topUp]);

  const currentId = ids && index < ids.length ? ids[index] : null;
  const current = currentId !== null ? results[currentId] : null;
  const photo = current && typeof current === 'object' && 'id' in current ? current : null;

  // Reset button selections whenever a new photo arrives for review
  useEffect(() => {
    if (photo) {
      setPicked(
        Object.fromEntries(AXES.map((a) => [a, photo[a] ?? ''])) as unknown as Record<Axis, string>
      );
    }
  }, [photo]);

  const advance = useCallback(() => {
    if (!ids) return;
    const next = index + 1;
    setIndex(next);
    setPicked(null);
    topUp(ids, next);
  }, [ids, index, topUp]);

  async function confirm() {
    if (!photo || !picked) return;
    const wasFixed = AXES.some((a) => picked[a] !== photo[a]);
    await fetch(`/api/photos/${photo.id}/correct`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(picked),
    });
    setStats((s) => ({
      reviewed: s.reviewed + 1,
      right: s.right + (wasFixed ? 0 : 1),
      fixed: s.fixed + (wasFixed ? 1 : 0),
    }));
    advance();
  }

  function retryCurrent() {
    if (currentId === null || !ids) return;
    launchedIds.current.delete(currentId);
    setResults((p) => {
      const next = { ...p };
      delete next[currentId];
      return next;
    });
    topUp(ids, index);
  }

  async function classifyRest() {
    await fetch('/api/classify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'start' }),
    });
    onFinish(
      `Training done — ${stats.right}/${stats.reviewed} right first try. Classifying the rest of the folder with your training.`
    );
  }

  const readyAhead = ids
    ? ids.slice(index + 1).filter((id) => {
        const r = results[id];
        return !!r && typeof r === 'object' && 'id' in r;
      }).length
    : 0;
  const wasFixed = photo && picked ? AXES.some((a) => picked[a] !== photo[a]) : false;
  const pct = stats.reviewed ? Math.round((100 * stats.right) / stats.reviewed) : null;
  const finished = ids !== null && ids.length > 0 && index >= ids.length;
  const nothingToDo = ids !== null && ids.length === 0;

  function tickColor(i: number): string {
    if (i < index) return BLUE;
    if (i === index) return 'rgb(150,180,230)';
    const r = ids ? results[ids[i]] : undefined;
    if (r && typeof r === 'object' && 'id' in r) return 'rgb(190,205,235)';
    return '#E5E7EB';
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-[880px] max-w-full max-h-[94vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: 'rgb(38,38,38)' }}>
          <span className="text-white font-semibold">
            Training — photo {ids ? Math.min(index + 1, ids.length) : '…'} of {ids?.length ?? '…'}
          </span>
          <div className="flex items-center gap-4 text-sm text-white/80">
            {pct !== null && <span>{pct}% right so far</span>}
            {stats.fixed > 0 && (
              <span className="text-orange-300">
                {stats.fixed} lesson{stats.fixed === 1 ? '' : 's'} taught
              </span>
            )}
            {readyAhead > 0 && !finished && (
              <span className="text-white/50">{readyAhead} ready ahead</span>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white" title="Close (progress is saved)">✕</button>
          </div>
        </div>
        <div style={{ height: 4, backgroundColor: BLUE }} />

        {/* Progress ticks */}
        {ids && ids.length > 0 && (
          <div className="flex gap-0.5 px-4 pt-3">
            {ids.map((_, i) => (
              <div key={i} className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: tickColor(i) }} />
            ))}
          </div>
        )}

        {fatal && (
          <Center>
            <div className="text-red-600 text-sm mb-3">{fatal}</div>
            <button onClick={onClose} className="px-4 py-2 rounded border border-gray-300 text-sm">Close</button>
          </Center>
        )}

        {!fatal && ids === null && <Center>Sampling {sampleSize} random photos from the folder…</Center>}

        {nothingToDo && (
          <Center>
            <div className="text-lg font-semibold mb-2">Nothing new to train on</div>
            <div className="text-gray-600 text-sm mb-5 max-w-md text-center">
              Every photo in this folder has already been reviewed. Pick a different
              folder, or use the grid below to re-check existing answers.
            </div>
            <button onClick={onClose} className="px-4 py-2 rounded border border-gray-300 text-sm">Close</button>
          </Center>
        )}

        {!fatal && !finished && currentId !== null && (!current || current === 'loading') && (
          <Center>
            <Spinner />
            <div className="mt-3 text-gray-600">Analyzing photo {index + 1}…</div>
            {readyAhead > 0 && (
              <div className="mt-1 text-xs text-gray-400">{readyAhead} more already analyzed behind it</div>
            )}
          </Center>
        )}

        {!fatal && !finished && current && typeof current === 'object' && !('id' in current) && (
          <Center>
            <div className="text-red-600 text-sm mb-3">{current.error}</div>
            <div className="flex gap-2">
              <button onClick={advance} className="px-4 py-2 rounded border border-gray-300 text-sm">Skip this photo</button>
              <button onClick={retryCurrent} className="px-4 py-2 rounded text-white text-sm" style={{ backgroundColor: BLUE }}>
                Retry
              </button>
            </div>
          </Center>
        )}

        {!finished && photo && picked && (
          <div className="flex-1 overflow-y-auto p-4 flex flex-col md:flex-row gap-4">
            <div className="md:w-1/2">
              {photo.preview && (
                <img
                  src={`/api/preview/${photo.preview}`}
                  alt=""
                  className="w-full rounded-lg border border-gray-200 object-contain max-h-[62vh]"
                />
              )}
              <div className="font-mono text-xs text-gray-400 mt-1 truncate">{photo.path.split('/').pop()}</div>
            </div>
            <div className="md:w-1/2 flex flex-col gap-3">
              {AXES.map((a) => {
                const isProduct = a === 'product';
                const pickedList = isProduct ? picked[a].split(' + ') : [picked[a]];
                const modelList = isProduct ? (photo[a] ?? '').split(' + ') : [photo[a]];
                const toggle = (v: string) => {
                  if (!isProduct) {
                    setPicked({ ...picked, [a]: v });
                    return;
                  }
                  const next = pickedList.includes(v)
                    ? pickedList.filter((p) => p !== v)
                    : [...pickedList, v];
                  setPicked({
                    ...picked,
                    product: config.axes.product.filter((p) => next.includes(p)).join(' + '),
                  });
                };
                return (
                  <div key={a}>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                      {a}
                      {isProduct && <span className="normal-case text-gray-300"> — tap all that are featured</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {config.axes[a].map((v) => {
                        const selected = pickedList.includes(v);
                        const wasModel = modelList.includes(v);
                        return (
                          <button
                            key={v}
                            onClick={() => toggle(v)}
                            className={`px-3 py-2 rounded-md text-sm border transition-colors ${
                              selected
                                ? 'text-white border-transparent'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                            style={selected ? { backgroundColor: !wasModel ? 'rgb(234, 88, 12)' : BLUE } : undefined}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <div className="mt-auto flex gap-2 pt-3">
                <button onClick={advance} className="px-4 py-3 rounded-md border border-gray-300 text-sm">
                  Skip
                </button>
                <button
                  onClick={confirm}
                  disabled={!picked.product}
                  className="flex-1 py-3 rounded-md text-white font-semibold disabled:opacity-40"
                  style={{ backgroundColor: wasFixed ? 'rgb(234, 88, 12)' : 'rgb(22, 163, 74)' }}
                >
                  {!picked.product
                    ? 'Pick at least one product'
                    : wasFixed
                      ? 'Save fix & teach → next photo'
                      : '✓ Correct → next photo'}
                </button>
              </div>
            </div>
          </div>
        )}

        {finished && (
          <Center>
            <div className="text-2xl font-bold mb-2">Training session done</div>
            <div className="text-gray-600 mb-1">
              {stats.right} of {stats.reviewed} right on the first try
              {pct !== null ? ` (${pct}%)` : ''} · {stats.fixed} lesson{stats.fixed === 1 ? '' : 's'} taught
            </div>
            <div className="text-xs text-gray-400 mb-5">
              Lessons are stored centrally and apply to every folder from now on.
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded border border-gray-300 text-sm">Close</button>
              {ids && totalInFolder > ids.length && (
                <button onClick={classifyRest} className="px-5 py-2 rounded-md text-white text-sm font-medium" style={{ backgroundColor: BLUE }}>
                  Classify the remaining {totalInFolder - ids.length} photos
                </button>
              )}
            </div>
          </Center>
        )}
      </div>
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-10 min-h-[320px] text-sm">
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div
      className="w-8 h-8 rounded-full border-4 border-gray-200 animate-spin"
      style={{ borderTopColor: BLUE }}
    />
  );
}
